import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { AlertCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const fC = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const DIV_LABEL = {
  lc: 'LC', 'cc-clubs': 'CC Loyalty', 'cc-ops': 'CC Residential',
  cs: 'Club Social', me: 'MindEscapes', tdb: 'TDB', 'club-ops': 'Club Ops',
};

export default function TermsPendingWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get('/contracts/terms-pending').then(r => setData(r.data)).catch(() => setData({ count: 0, items: [] }));
  }, []);

  if (!data || data.count === 0) return null;

  const totalValue = data.items.reduce((s, c) => s + (c.annual_value || 0), 0);
  const visible = expanded ? data.items : data.items.slice(0, 5);

  const openContract = async (c) => {
    // Resolve to client dossier by client_name + division_id for deep link.
    try {
      const r = await api.get(`/clients-registry?division_id=${c.division_id}`);
      const match = (r.data || []).find(cl =>
        (cl.client_name || '').toLowerCase().trim() === (c.client_name || '').toLowerCase().trim()
        || (cl.client_name || '').toLowerCase().includes((c.client_name || '').toLowerCase())
        || (c.client_name || '').toLowerCase().includes((cl.client_name || '').toLowerCase())
      );
      if (match) {
        navigate(`/client/${match.id}`);
      } else {
        alert(`No client dossier found for "${c.client_name}". Create one from the division Clients tab first.`);
      }
    } catch {
      alert('Could not resolve client');
    }
  };

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: '#D97706', backgroundColor: '#FFFBEB' }} data-testid="terms-pending-widget">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2">
          <AlertCircle size={18} style={{ color: '#D97706' }} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#78350F' }}>Payment Terms Setup — {data.count} contract{data.count === 1 ? '' : 's'} pending</p>
            <p className="text-[11px]" style={{ color: '#92400E' }}>
              Total annual value: <strong>{fC(totalValue)}</strong> · define terms so schedules auto-build & revenue flows to Realised
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-1">
        {visible.map(c => (
          <button key={c.id} onClick={() => openContract(c)} className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-white hover:bg-[#F7F5F2] border" style={{ borderColor: '#FDE68A' }} data-testid={`terms-pending-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium truncate" style={{ color: '#1B2A4A' }}>{c.client_name}</p>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
                  {DIV_LABEL[c.division_id] || c.division_id?.toUpperCase()}
                </span>
                {c.entity && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>· {c.entity}</span>}
              </div>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                Annual <strong style={{ color: '#1B2A4A' }}>{fC(c.annual_value)}</strong>
                {c.start_date && ` · Starts ${c.start_date}`}
                {c.schedule_rows === 0 && ' · No schedule yet'}
              </p>
            </div>
            <ArrowRight size={14} style={{ color: '#D97706' }} className="flex-shrink-0" />
          </button>
        ))}
      </div>

      {data.items.length > 5 && (
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-[11px] mx-auto" style={{ color: '#92400E' }} data-testid="terms-pending-toggle">
          {expanded
            ? (<>Hide <ChevronUp size={11} /></>)
            : (<>Show {data.items.length - 5} more <ChevronDown size={11} /></>)}
        </button>
      )}
    </div>
  );
}
